import { ratesInterface } from '../interfaces';
import { MainProductType, Product } from '../modules/ShoppingCart/interfaces';
import {
  calcDpTotalUtil,
  DpPricePart,
  DpTotalResult,
  hasSameCurrency,
} from './currencyConversion';

type PaxCounts = { adult?: number; child?: number; infant?: number };

const getFlightLegPrice = (
  flightInfo: {
    price?: {
      adult?: number;
      child?: number;
      infant?: number;
      total?: number;
      grandTotal?: number;
      currency_sign?: string;
    };
    flightFrom?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
    flightBack?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
  },
  pax: PaxCounts
): number => {
  const suitcasePrice =
    flightInfo?.flightFrom?.baggage?.suitcase_price ||
    flightInfo?.flightBack?.baggage?.suitcase_price ||
    0;
  const trollyPrice =
    flightInfo?.flightFrom?.baggage?.trolly_price ||
    flightInfo?.flightBack?.baggage?.trolly_price ||
    0;

  const calculatedTotalPrice =
    Number(pax.adult ?? 0) * (flightInfo?.price?.adult ?? 0) +
    Number(pax.child ?? 0) * (flightInfo?.price?.child ?? 0) +
    Number(pax.infant ?? 0) * (flightInfo?.price?.infant ?? 0) +
    suitcasePrice +
    trollyPrice;

  return (
    flightInfo?.price?.grandTotal ??
    flightInfo?.price?.total ??
    calculatedTotalPrice
  );
};

/** Currency sign a cart line (product or sub-product) is priced in. */
export const getCartLineCurrency = (
  product: Product,
  cartCurrencySign?: string
): string | undefined => {
  if (product.base_currency_sign) {
    return product.base_currency_sign;
  }

  const data = product.product_data;

  if (product.type === MainProductType.DynamicPackage) {
    return data?.currency_sign ?? cartCurrencySign;
  }

  if (
    product.type === MainProductType.oneWayFlight ||
    product.type === MainProductType.Flight
  ) {
    return (
      data?.singleFlight?.flightsInfo?.price?.currency_sign ??
      data?.price?.currency_sign ??
      data?.flightsInfo?.price?.currency_sign
    );
  }

  if (product.type === MainProductType.Event || product.type === MainProductType.EventTicket) {
    return data?.currency ?? data?.ticketDetails?.currency ?? cartCurrencySign;
  }

  if (product.type === MainProductType.Room) {
    return data?.currency_sign ?? cartCurrencySign;
  }

  if (product.type === MainProductType.Hotel) {
    return (
      data?.currency_sign ??
      data?.pricingInfo?.currency_sign ??
      cartCurrencySign
    );
  }

  if (
    product.type === MainProductType.Flights ||
    product.type === MainProductType.oneWayFlights
  ) {
    return data?.flightsInfo?.price?.currency_sign ?? cartCurrencySign;
  }

  return (
    data?.price?.currency_sign ??
    data?.flightsInfo?.price?.currency_sign ??
    data?.currency_sign ??
    cartCurrencySign
  );
};

const pushLinePart = (
  parts: DpPricePart[],
  amount: number | undefined,
  currency: string | undefined
) => {
  if (!Number.isFinite(Number(amount))) {
    return;
  }
  parts.push({ amount: Number(amount), currency });
};

/** Billable cart lines for gross or net totals (one entry per priced row). */
export const collectCartBillableParts = (
  products: Product[] | undefined,
  cartCurrencySign: string | undefined,
  field: 'total_price' | 'total_net_price'
): DpPricePart[] => {
  const parts: DpPricePart[] = [];

  products?.forEach((product) => {
    const lineAmount = product[field];

    if (lineAmount > 0) {
      pushLinePart(
        parts,
        lineAmount,
        getCartLineCurrency(product, cartCurrencySign)
      );
      return;
    }

    product.subProducts?.forEach((subProduct) => {
      const subAmount = subProduct[field];
      if (subAmount > 0) {
        pushLinePart(
          parts,
          subAmount,
          getCartLineCurrency(subProduct, cartCurrencySign)
        );
      }
    });
  });

  return parts;
};

export const calculateCartTotalsUtil = ({
  products,
  cartCurrencySign,
  rates,
}: {
  products: Product[] | undefined;
  cartCurrencySign?: string;
  rates?: ratesInterface;
}): {
  gross: DpTotalResult;
  net: DpTotalResult;
} => {
  const grossParts = collectCartBillableParts(
    products,
    cartCurrencySign,
    'total_price'
  );
  const netParts = collectCartBillableParts(
    products,
    cartCurrencySign,
    'total_net_price'
  );

  if (!rates || grossParts.length === 0) {
    const grossRaw = grossParts.reduce(
      (sum, part) => sum + Number(part.amount),
      0
    );
    const netRaw = netParts.reduce((sum, part) => sum + Number(part.amount), 0);
    const fallbackCurrency = cartCurrencySign ?? '$';
    return {
      gross: { total: Math.ceil(grossRaw), currency: fallbackCurrency },
      net: { total: Math.ceil(netRaw), currency: fallbackCurrency },
    };
  }

  return {
    gross: calcDpTotalUtil({ parts: grossParts, rates }),
    net: calcDpTotalUtil({ parts: netParts, rates }),
  };
};

/** Combined total for multiple flight legs (search / view / cart). */
export const calcFlightsTotalUtil = ({
  flights,
  pax,
  rates,
}: {
  flights: Array<{
    price?: {
      adult?: number;
      child?: number;
      infant?: number;
      total?: number;
      grandTotal?: number;
      currency_sign?: string;
    };
    flightFrom?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
    flightBack?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
  }>;
  pax: PaxCounts;
  rates: ratesInterface;
}): DpTotalResult => {
  const parts: DpPricePart[] = flights.map((flight) => ({
    amount: getFlightLegPrice(flight, pax),
    currency: flight.price?.currency_sign,
  }));

  return calcDpTotalUtil({ parts, rates });
};

export const flightsHaveMixedCurrencies = (
  flights: Array<{ price?: { currency_sign?: string } }>
): boolean => {
  if (!flights?.length) {
    return false;
  }
  const first = flights[0]?.price?.currency_sign;
  if (!first) {
    return false;
  }
  return !flights.every((flight) =>
    hasSameCurrency(flight?.price?.currency_sign ?? '', first)
  );
};
